import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronUp, MessageSquare } from "lucide-react";
import api from "../api";
import { Spinner, Pill, relTime } from "../ui";

const STAGES = [
  { id: "", label: "All" },
  { id: "OPENED", label: "In review" },
  { id: "ANALYZED", label: "Analyzed" },
  { id: "DECIDED", label: "Decided" },
  { id: "RESOLVED", label: "Resolved" },
];

const SORTS = [
  { id: "recent", label: "Recent" },
  { id: "top", label: "Top" },
  { id: "discussed", label: "Discussed" },
];

const stageTone = { OPENED: "accent", ANALYZED: "accent", DECIDED: "success", RESOLVED: "success" };
const stageLabel = { OPENED: "In review", ANALYZED: "Analyzed", DECIDED: "Decided", RESOLVED: "Resolved" };

export default function Archive() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [sort, setSort] = useState("recent");

  useEffect(() => {
    const t = setTimeout(() => {
      const params = { sort, limit: 40 };
      if (q) params.q = q;
      if (stage) params.stage = stage;
      api.get("/archive", { params }).then((r) => setData(r.data)).catch(() => setData({ items: [] }));
    }, 250);
    return () => clearTimeout(t);
  }, [q, stage, sort]);

  const items = useMemo(() => data?.items || [], [data]);

  return (
    <div data-testid="archive-page">
      <p className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-[#B5A58A]/80 mb-4">The public column</p>
      <h1 className="font-serif-display font-light tracking-tighter text-5xl md:text-6xl text-white mb-4">Archive</h1>
      <p className="text-sm leading-relaxed text-stone-400 max-w-xl mb-10">
        Real cases, deeply anonymized. Read how others wrestled with their decisions — and lend a perspective where you've lived it.
      </p>

      <input
        data-testid="archive-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by case number, title, or context"
        className="w-full bg-[#26241F] border border-white/[0.08] rounded-xl px-5 py-4 text-sm text-white placeholder:text-stone-500 outline-none focus:border-[#B5A58A]/50 transition-colors duration-300 mb-5"
      />

      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex gap-2 flex-wrap">
          {STAGES.map((s) => (
            <button
              key={s.id}
              data-testid={`archive-stage-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setStage(s.id)}
              className={`rounded-full px-4 py-2 font-mono-label text-[9px] tracking-[0.2em] uppercase border transition-colors duration-300 ${
                stage === s.id ? "border-[#B5A58A] text-[#B5A58A]" : "border-white/[0.12] text-stone-400 hover:text-white"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {SORTS.map((s) => (
            <button
              key={s.id}
              data-testid={`archive-sort-${s.id}`}
              onClick={() => setSort(s.id)}
              className={`font-mono-label text-[9px] tracking-[0.2em] uppercase px-3 py-2 transition-colors duration-300 ${
                sort === s.id ? "text-[#B5A58A] border-b border-[#B5A58A]" : "text-stone-500 hover:text-white"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <Spinner label="Opening the column…" />
      ) : items.length === 0 ? (
        <p className="text-sm text-stone-500" data-testid="archive-empty">No cases found.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((c) => (
            <Link
              key={c.public_case_number}
              to={`/app/archive/${c.public_case_number}`}
              data-testid={`archive-card-${c.public_case_number}`}
              className="group bg-[#26241F] border border-white/[0.08] rounded-2xl p-6 hover:border-[#B5A58A]/40 transition-colors duration-300"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="font-mono-label text-[9px] tracking-[0.25em] text-[#B5A58A]/80">{c.public_case_number}</p>
                <Pill tone={stageTone[c.stage] || "neutral"}>{stageLabel[c.stage] || c.stage}</Pill>
              </div>
              <h3 className="font-serif-display font-light tracking-tight text-2xl text-white mb-3 leading-snug">{c.title}</h3>
              <p className="text-sm leading-relaxed text-stone-400 line-clamp-3 mb-4">{c.situation_excerpt}</p>
              <div className="flex items-center gap-4 font-mono-label text-[9px] tracking-[0.15em] uppercase text-stone-500">
                <span>{c.advisor?.name || "Board"}</span>
                <span className="flex items-center gap-1"><ChevronUp className="w-3 h-3" strokeWidth={1.5} />{c.upvote_count || 0}</span>
                <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" strokeWidth={1.5} />{c.comment_count || 0}</span>
                <span>{relTime(c.updated_at)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
